import { map, pipe } from "wonka";
import type { Exchange, Operation } from "@urql/vue";
import { CACHEABLE_OPERATIONS, getCacheableEntry } from "@f3/shared";
import {
  USER_IDENTITY_HEADER,
  AUTH_CHANNEL_HEADER,
  APQ_PREFLIGHT_HEADER,
} from "@f3/shared/identity";
import { getOperationName } from "./urql";
import { getSharedHeaders } from "./headers";

/**
 * Derive the cacheable operation names from the shared constant.
 * The shared constant (packages/shared/src/apq-cacheable-operations.ts) is
 * the single source of truth for both frontend and backend.
 */
const CACHEABLE_QUERY_OPERATIONS = new Set(Object.keys(CACHEABLE_OPERATIONS));

/**
 * Operations that return data stable enough for cache-first on repeat visits.
 * Derived from CACHEABLE_OPERATIONS: all ops with TTL >= 30s qualify.
 *
 * With cache-first, URQL returns cached data immediately and does NOT send a
 * network request unless the document cache is empty (first visit). This
 * eliminates duplicate requests for theme, channel config, and chat rooms
 * on repeat visits or when navigating back to a show page.
 *
 * Fast-changing operations like GetQuizzesForChannel (10s TTL) stay on
 * cache-and-network to ensure fresh data.
 *
 * NEVER hand-roll this set — add/update TTLs in packages/shared/src/apq-cacheable-operations.ts.
 * The CI test apq-cacheable-operations-sync.test.ts verifies no drift at build time.
 */
export const CACHE_FIRST_OPERATIONS = new Set(
  Object.keys(CACHEABLE_OPERATIONS).filter((name) => {
    const ttl = getCacheableEntry(name)?.ttl;
    return ttl !== undefined && ttl >= 30;
  }),
);

/**
 * Returns true if an operation should use APQ GET (cacheable by CDN).
 * Returns false for mutations, user-specific queries, and paginated queries.
 */
export function isCacheableOperation(
  operationName: string,
  operationType: string,
): boolean {
  if (operationType !== "query") return false;
  return CACHEABLE_QUERY_OPERATIONS.has(operationName);
}

/**
 * Returns fetchOptions with auth headers stripped.
 * Used for cacheable queries so CloudFront doesn't create per-user cache
 * entries via Vary: Authorization.
 */
function getCacheableFetchOptions(): RequestInit {
  const headers: Record<string, string> = { ...getSharedHeaders() };
  delete headers[USER_IDENTITY_HEADER];
  delete headers[AUTH_CHANNEL_HEADER];
  headers[APQ_PREFLIGHT_HEADER] = "true";
  return { headers };
}

/**
 * Exchange that controls GET vs POST per-operation for APQ, and sets
 * cache-first request policy for stable operations.
 *
 * Cacheable operations → GET (cached by CloudFront).
 * Non-cacheable operations → POST (bypasses CloudFront cache).
 *
 * For cacheable operations, auth headers are stripped from fetchOptions
 * so CloudFront doesn't create per-user cache entries.
 *
 * For stable cache-first operations, requestPolicy is overridden to
 * "cache-first" so repeat visits don't trigger redundant network requests.
 * The context-level requestPolicy takes precedence over the client default.
 */
export const cacheClassificationExchange: Exchange =
  ({ forward }) =>
  (ops$) => {
    return pipe(
      ops$,
      map((operation: Operation) => {
        const opName = getOperationName(operation);
        const opType = operation.kind;

        if (isCacheableOperation(opName, opType)) {
          const isCacheFirst =
            CACHE_FIRST_OPERATIONS.has(opName) && opType === "query";
          return {
            ...operation,
            context: {
              ...operation.context,
              preferGetMethod: "within-url-limit" as const,
              fetchOptions: getCacheableFetchOptions,
              ...(isCacheFirst && { requestPolicy: "cache-first" as const }),
            },
          };
        }

        return operation;
      }),
      forward,
    );
  };
